/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { QRCodeSVG } from "qrcode.react";

import { useFlag } from "@superweb/flags";
import { useTypo, useUiColors } from "@superweb/ui";

import { Message, useMessage } from "#intl";

import { EmptyStatePlaceholder } from "./empty-state-placeholder";
import { useErrorBoundaryOptions } from "./error-boundary-context";

export const Unexpected = ({
  reset,
  variant,
  traceId,
  clientDebugId,
  titleId,
  size,
}: {
  reset?: () => void;
  variant?: "waitingMan";
  traceId?: string;
  clientDebugId?: string;
  titleId?: string;
  size?: "small" | "medium";
}) => {
  const uiColors = useUiColors();
  const typo = useTypo();
  const message = useMessage();
  const flag = useFlag();

  const enableSupportLink = flag({
    id: "e0e4ee31-9daf-46a1-9a3c-70aa70ee686a",
    description: "Enable support link in unexpected error",
  });

  const supportChatCreate = flag({
    id: "7eb2acf5-71fd-47d3-aea8-5d100b6f5676",
    description: "Support chat view",
  });

  const { makeSupportLink } = useErrorBoundaryOptions();

  const supportLink = makeSupportLink?.({
    traceId,
    clientDebugId,
  });

  const qrValue = traceId || clientDebugId;

  const shouldShowLink =
    enableSupportLink &&
    supportChatCreate &&
    supportLink &&
    (clientDebugId || traceId);

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <EmptyStatePlaceholder
        {...(enableSupportLink && qrValue
          ? {
              image: (
                <div
                  css={{
                    display: "grid",
                    alignContent: "start",
                    justifyItems: "center",
                  }}
                >
                  <QRCodeSVG
                    value={qrValue}
                    level="L"
                    size={176}
                    bgColor="transparent"
                    fgColor={uiColors.textMinor}
                  />
                </div>
              ),
            }
          : variant
            ? { variant }
            : { image: null })}
        size={size}
        title={
          <Message
            id="63cee1c0-f1bd-4245-9a6c-3cc0245fe030"
            context="Common error. Something went wrong. Title"
            default="Something went wrong"
          />
        }
        titleId={titleId}
        description={
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              rowGap: "12px",
              maxWidth: "548px",
            }}
          >
            <Message
              id="ada3741a-6c41-4510-9050-8a0c3e6dd1f8"
              context="Common error. Something went wrong. Info"
              default="Try reloading, check back later, or contact Support"
            />
            {(traceId || clientDebugId) && (
              <span
                css={{
                  color: uiColors.textMinor,
                  ...typo({
                    level: "body2",
                    density: "normal",
                    weight: "regular",
                  }),
                }}
              >
                {traceId || clientDebugId}
              </span>
            )}
          </div>
        }
        buttons={[
          ...(reset
            ? [
                {
                  view: shouldShowLink ? "default" : "outline",
                  text: message({
                    id: "0597cc88-4cf5-4bec-bc13-80ef1e98ef26",
                    context:
                      "Common error. Something went wrong. Refresh button title",
                    default: "Reload",
                  }),
                  onPress: reset,
                } as const,
              ]
            : []),
          ...(shouldShowLink
            ? [
                {
                  view: "outline",
                  text: message({
                    id: "25a4f20c-3bb5-45c1-8d11-d2a109402616",
                    context:
                      "Common error. Something went wrong. Support link title",
                    default: "Support",
                  }),
                  link: {
                    href: supportLink.href,
                    external: true,
                  },
                } as const,
              ]
            : []),
        ]}
      />
    </div>
  );
};
